import type React from "react";
import { Box, Typography, Divider } from "@mui/material";

interface TimeData {
  call_time: string;
  departure_time: string;
  arrival_at_venue_time: string;
  setup_from_time: string;
  setup_to_time: string;
  shoot_start_time: string;
  shoot_end_time: string;
  estimated_wrap_time: string;
  back_to_base_time: string;
  unpacking_time: string;
  depart_time: string;
}

interface ScheduleTimelineCardProps {
  timeData: TimeData;
}

interface SlotDef {
  id: string;
  label: string;
  subtitle: string;
  start: string;
  end: string;
}

function parseMinutes(t: string): number {
  if (!t) return -1;
  const parts = t.split(":").map(Number);
  if (parts.length < 2 || parts.some(isNaN)) return -1;
  return parts[0] * 60 + parts[1];
}

function fmt(t: string): string {
  if (!t) return "—";
  const parts = t.split(":");
  if (parts.length < 2) return t;
  return `${parts[0]}${parts[1]}`;
}

function calcDuration(start: string, end: string): number | null {
  const s = parseMinutes(start);
  const e = parseMinutes(end);
  if (s === -1 || e === -1) return null;
  const diff = e - s;
  return diff > 0 ? diff : null;
}

function isCurrentlyActive(start: string, end: string): boolean {
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const s = parseMinutes(start);
  const e = parseMinutes(end);
  if (s === -1 || e === -1) return false;
  return nowMinutes >= s && nowMinutes < e;
}

const ScheduleTimelineCard: React.FC<ScheduleTimelineCardProps> = ({
  timeData,
}) => {
  const slots: SlotDef[] = [
    {
      id: "call_time",
      label: "Call Time",
      subtitle: "Mobilizing the team",
      start: timeData.call_time,
      end: timeData.departure_time,
    },
    {
      id: "departure_time",
      label: "Departure",
      subtitle: "Travelling to venue",
      start: timeData.departure_time,
      end: timeData.arrival_at_venue_time,
    },
    {
      id: "arrival_at_venue_time",
      label: "Arrival Time",
      subtitle: "Arriving at the venue",
      start: timeData.arrival_at_venue_time,
      end: timeData.setup_from_time,
    },
    {
      id: "setup_from_time",
      label: "Setting Up",
      subtitle: "Preparing equipment",
      start: timeData.setup_from_time,
      end: timeData.setup_to_time,
    },
    {
      id: "shoot_start_time",
      label: "Shoot Start",
      subtitle: "Live event begins",
      start: timeData.shoot_start_time,
      end: timeData.shoot_end_time,
    },
    {
      id: "shoot_end_time",
      label: "Shoot End",
      subtitle: "Wrapping production",
      start: timeData.shoot_end_time,
      end: timeData.estimated_wrap_time,
    },
    {
      id: "estimated_wrap_time",
      label: "Estimated Wrap",
      subtitle: "Pack-down begins",
      start: timeData.estimated_wrap_time,
      end: timeData.back_to_base_time,
    },
    {
      id: "back_to_base_time",
      label: "Back to Base",
      subtitle: "Returning to base",
      start: timeData.back_to_base_time,
      end: timeData.unpacking_time,
    },
    {
      id: "unpacking_time",
      label: "Unpacking",
      subtitle: "Unloading equipment",
      start: timeData.unpacking_time,
      end: timeData.depart_time,
    },
    {
      id: "depart_time",
      label: "Depart",
      subtitle: "End of schedule",
      start: timeData.depart_time,
      end: "",
    },
  ].filter((s) => !!s.start);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {slots.map((slot) => {
        const duration = calcDuration(slot.start, slot.end);
        const active = isCurrentlyActive(slot.start, slot.end);
        const startFmt = fmt(slot.start);
        const endFmt = slot.end ? fmt(slot.end) : null;

        return (
          <Box
            key={slot.id}
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "16px",
              border: active ? "1.5px solid #c8a898" : "1px solid #e5e7eb",
              backgroundColor: active ? "#f5ede6" : "#ffffff",
              overflow: "hidden",
              px: 2,
              py: 1.5,
              gap: 2,
            }}
          >
            {/* Duration column */}
            <Box
              sx={{
                minWidth: 52,
                textAlign: "center",
                flexShrink: 0,
              }}
            >
              {duration !== null ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      lineHeight: 1,
                      color: active ? "#7c4a35" : "#1a1a1a",
                    }}
                  >
                    {duration}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.95rem", color: "#9ca3af", mt: 0.25 }}
                  >
                    minutes
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: "0.9rem", color: "#9ca3af" }}>
                  —
                </Typography>
              )}
            </Box>

            {/* Vertical divider */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: active ? "#c8a898" : "#e5e7eb",
                alignSelf: "stretch",
              }}
            />

            {/* Label + subtitle */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: active ? "#4a2210" : "#111827",
                  lineHeight: 1.3,
                }}
              >
                {slot.label}
              </Typography>
              <Typography
                sx={{ fontSize: "0.95rem", color: "#9ca3af", mt: 0.25 }}
              >
                {slot.subtitle}
              </Typography>
            </Box>

            {/* Time range */}
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                color: active ? "#7c4a35" : "#6b7280",
                flexShrink: 0,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {endFmt ? `${startFmt}–${endFmt}` : startFmt}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default ScheduleTimelineCard;
